import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Database,
  Save,
  ArrowLeft,
  Loader2,
  FileText,
  Building2,
  Activity,
  Shield
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { brandsService } from '@/services/brands'
import { ROUTES } from '@/utils/constants'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'

const DataInputCreate = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  // Fetch brands for dropdown
  const {
    data: brands = [],
    isLoading: brandsLoading
  } = useQuery(
    'brands-for-select',
    () => brandsService.getForSelect(),
    {
      onError: (error) => {
        toast.error('Failed to load brands')
        console.error('Error loading brands:', error)
      }
    }
  )

  // Create data input mutation
  const createDataInputMutation = useMutation(
    (dataInputData) => dataInputsService.create(dataInputData),
    {
      onSuccess: (newDataInput) => {
        toast.success('Data input created successfully!')
        queryClient.invalidateQueries('dataInputs')
        navigate(`/data-inputs/${newDataInput.id}`)
      },
      onError: (error) => {
        toast.error('Failed to create data input')
        console.error('Error creating data input:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    // Convert brand_id to integer
    const dataInputData = {
      ...data,
      brand_id: parseInt(data.brand_id),
      is_active: data.is_active || true
    }
    createDataInputMutation.mutate(dataInputData)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(ROUTES.DATA_INPUTS)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Data Inputs
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Data Input</h1>
            <p className="mt-1 text-sm text-gray-600">
              Add a new data input to collect and manage data
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Data Input Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Basic information about the data input
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Data Input Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Data Input Name *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Database className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('name', {
                    required: 'Data input name is required',
                    minLength: {
                      value: 2,
                      message: 'Data input name must be at least 2 characters',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Data input name must be less than 100 characters',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter data input name"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Brand Selection */}
            <div>
              <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700">
                Brand *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  {...register('brand_id', {
                    required: 'Brand selection is required',
                  })}
                  className={`input pl-10 ${
                    errors.brand_id ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  disabled={brandsLoading}
                >
                  <option value="">Select a brand</option>
                  {brands.map((brand) => (
                    <option key={brand.value} value={brand.value}>
                      {brand.label}
                    </option>
                  ))}
                </select>
              </div>
              {errors.brand_id && (
                <p className="mt-1 text-sm text-red-600">{errors.brand_id.message}</p>
              )}
              {brandsLoading && (
                <p className="mt-1 text-sm text-gray-500">Loading brands...</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register('description', {
                    maxLength: {
                      value: 255,
                      message: 'Description must be less than 255 characters',
                    },
                  })}
                  rows={4}
                  className={`input pl-10 resize-none ${
                    errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter data input description"
                />
              </div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  {...register('is_active')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active Data Input</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Inactive data inputs are hidden from users and cannot collect new data
              </p>
            </div>

            {/* User Isolation - Only show for admin and manager roles */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div>
                <label className="flex items-center">
                  <input
                    {...register('enable_user_isolation')}
                    type="checkbox"
                    defaultChecked={false}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div className="ml-2 flex items-center">
                    <Shield className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-700">Enable User Isolation</span>
                  </div>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, users with 'user' role can only see records they created themselves. Admin and manager roles can see all records regardless of this setting.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(ROUTES.DATA_INPUTS)}
            className="btn-outline btn-md"
            disabled={createDataInputMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={createDataInputMutation.isLoading}
          >
            {createDataInputMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Data Input
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default DataInputCreate
